import { ghostBtnClass, inputClass, salesIndigoPanelClass } from "@/components/common/surfaceClasses";
import { useMemo } from "react";
import type { Lead, LeadFollowUp } from "@/types/lead";
import { SalesConsoleCollapsibleSection } from "./SalesConsoleCollapsibleSection";
import { SalesConsoleCustomerCalendarCuePanel } from "./SalesConsoleCustomerCalendarCuePanel";
import { SalesConsoleCustomerCalendarEventsPanel } from "./SalesConsoleCustomerCalendarEventsPanel";
import { useGoogleCalendarUpcoming } from "@/hooks/useGoogleCalendarUpcoming";

type Props = {
  leads: Lead[];
  leadId: string;
  onLeadIdChange: (leadId: string) => void;
  onViewLead: (lead: Lead) => void;
  onCreateFollowUp: (leadId: string, draft: { next_action: string; due_date: string; reason: string }) => void;
  onSaveFollowUp: (
    leadId: string,
    followUpId: string,
    patch: Partial<{ next_action: string; due_date: string; reason: string; status: LeadFollowUp["status"] }>
  ) => void;
};

export function SalesConsoleCustomerCalendarSection(props: Props) {
  const leadOptions = useMemo(
    () =>
      props.leads
        .filter((l) => !l.isTest)
        .sort((a, b) => b.lastUpdated - a.lastUpdated)
        .slice(0, 40),
    [props.leads]
  );

  const selected = useMemo(
    () => leadOptions.find((l) => l.id === props.leadId) ?? null,
    [leadOptions, props.leadId],
  );
  const followUps = useMemo(() => selected?.follow_ups ?? [], [selected]);
  const gcal = useGoogleCalendarUpcoming(true);

  function clearSelectedLead() {
    props.onLeadIdChange("");
  }

  return (
    <SalesConsoleCollapsibleSection
      title="Customer Calendar"
      subtitle="Create/edit events, transcript cues → official follow-ups"
      defaultOpen
      headerRight={
        <div className="flex flex-wrap items-center gap-2">
          {gcal.linked && gcal.eventsTodayCount > 0 ? (
            <span className="text-[10px] font-semibold rounded-full bg-emerald-100 text-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-200 px-2 py-0.5">
              {gcal.eventsTodayCount} in Google today
            </span>
          ) : null}
          {selected ? (
            <button
              type="button"
              className={`${ghostBtnClass} px-2.5 py-1 text-[11px] font-semibold`}
              onClick={clearSelectedLead}
            >
              Close lead
            </button>
          ) : null}
        </div>
      }
    >
      <div className={`${salesIndigoPanelClass} bg-indigo-50/50 space-y-3`}>
        <div className="grid gap-2 sm:grid-cols-2">
          <label className="text-xs text-gray-700 dark:text-zinc-300">
            Lead
            <select
              className={`mt-1 w-full ${inputClass} rounded-xl`}
              value={props.leadId}
              onChange={(e) => props.onLeadIdChange(e.target.value)}
            >
              <option value="">Select a lead…</option>
              {leadOptions.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} · {new Date(l.lastUpdated).toLocaleDateString()}
                </option>
              ))}
            </select>
          </label>
          {selected ? (
            <div className="flex flex-wrap items-end justify-end gap-2">
              <button type="button" className="text-xs text-blue-700 underline dark:text-blue-400" onClick={() => props.onViewLead(selected)}>
                View this lead in Leads mode
              </button>
            </div>
          ) : null}
        </div>

        {selected ? (
          <>
            <SalesConsoleCustomerCalendarCuePanel key={selected.id} lead={selected} onCreateFollowUp={props.onCreateFollowUp} />
            <SalesConsoleCustomerCalendarEventsPanel
              lead={selected}
              followUps={followUps}
              onCreateFollowUp={props.onCreateFollowUp}
              onSaveFollowUp={props.onSaveFollowUp}
            />
          </>
        ) : (
          <p className="text-xs text-gray-500 dark:text-zinc-400">Select a lead to manage events and transcript cues.</p>
        )}
      </div>
    </SalesConsoleCollapsibleSection>
  );
}
